<template>
  <div class="docs">
    <!-- La barra de arriba: el nombre, el buscador y la puerta de vuelta. La
         documentación se lee con el ERP abierto en la pestaña de al lado, así
         que «volver» es cerrar esta pestaña tantas veces como seguirla — el
         enlace está para quien llegó aquí sin más. -->
    <header class="docs__top">
      <RouterLink to="/help" class="docs__brand">
        <span class="docs__mark" aria-hidden="true">?</span>
        {{ t('help.title') }}
      </RouterLink>

      <!-- Buscar es la primera cosa que se hace en una documentación que ha
           crecido: nadie recorre un índice de veinticinco módulos buscando la
           palabra «archivar». Busca en los títulos y en los resúmenes, que es lo
           que está cargado — el cuerpo de cada tema se pide al abrirlo. -->
      <label class="docs__search">
        <span class="docs__searchIcon" aria-hidden="true">⌕</span>
        <input
          v-model="docsQuery"
          type="search"
          class="docs__searchInput"
          :placeholder="t('help.search')"
          :aria-label="t('help.search')"
        >
      </label>

      <RouterLink to="/" class="docs__back">{{ t('help.backToApp') }} →</RouterLink>
    </header>

    <div class="docs__body">
      <!-- El índice de la DOCUMENTACIÓN, no el menú del ERP. Son dos sitios
           distintos y confundirlos es lo que hace que alguien crea que se ha
           cambiado de pantalla cuando solo estaba leyendo. -->
      <nav class="docs__side" :aria-label="t('help.index')">
        <RouterLink to="/help" class="docs__link" :class="{ 'docs__link--on': !route.params.topic }">
          {{ t('help.all') }}
        </RouterLink>

        <p v-if="docsTerm && !groups.length" class="docs__none">{{ t('help.noMatch') }}</p>

        <template v-for="group in groups" :key="group.name">
          <div class="docs__group">{{ group.label }}</div>
          <RouterLink
            v-for="topic in group.topics" :key="topic.id"
            :to="`/help/${topic.id}`"
            class="docs__link"
            :class="{ 'docs__link--on': route.params.topic === topic.id }"
          >{{ t(topic.titleKey) }}</RouterLink>
        </template>
      </nav>

      <!-- Tres columnas y no dos. La del medio tiene la medida de un párrafo que
           se lee sin perder el renglón; la de la derecha es lo que hace que el
           ancho sobrante sirva para algo en vez de quedarse en blanco: los
           apartados de esta página, para saltar y para saber cuánto queda.

           El contenido va CENTRADO en lo que sobra. Pegado a la izquierda, en
           una pantalla ancha, la documentación parece una columna huérfana
           puesta en una esquina. -->
      <!-- El índice no es texto de lectura sino una rejilla de tarjetas, y una
           rejilla estrechada a la medida de un párrafo desperdicia la pantalla
           entera. La medida de lectura es para lo que se lee seguido. -->
      <div class="docs__reading" :class="{ 'docs__reading--wide': !route.params.topic }">
        <main ref="main" class="docs__main">
          <slot />

          <!-- Lo que cierra una página de documentación: a dónde se va después.
               Sin esto, terminar de leer un tema es quedarse mirando el final de
               la página y volver al índice con el botón de atrás. -->
          <nav v-if="around.prev || around.next" class="docs__around" :aria-label="t('help.index')">
            <RouterLink v-if="around.prev" :to="`/help/${around.prev.id}`" class="docs__step">
              <span class="docs__stepWay">← {{ t('help.previous') }}</span>
              <span class="docs__stepName">{{ t(around.prev.titleKey) }}</span>
            </RouterLink>
            <span v-else />

            <RouterLink v-if="around.next" :to="`/help/${around.next.id}`" class="docs__step docs__step--next">
              <span class="docs__stepWay">{{ t('help.next') }} →</span>
              <span class="docs__stepName">{{ t(around.next.titleKey) }}</span>
            </RouterLink>
          </nav>
        </main>

        <!-- Sólo en un tema. El índice general ya ES una lista de enlaces, y un
             índice de la lista debajo de la lista no orienta a nadie. -->
        <aside v-if="outline.length && route.params.topic" class="docs__outline" :aria-label="t('help.onThisPage')">
          <div class="docs__outlineTitle">{{ t('help.onThisPage') }}</div>
          <a
            v-for="item in outline" :key="item.id"
            :href="`#${item.id}`"
            class="docs__anchor"
            :class="[`docs__anchor--${item.depth}`, { 'docs__anchor--on': item.id === here }]"
            @click.prevent="jumpTo(item.id)"
          >{{ item.text }}</a>
        </aside>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { helpGroups } from '../app/help'
import { useMountedModules } from '../app/instance'
import { outlineOf } from '../app/helpOutline'
import { docsQuery, docsTerm, filterGroups } from '../app/docsSearch'

/**
 * El armazón de la documentación.
 *
 * Deliberadamente NO es el del ERP. Lo que se lee aquí se lee con la aplicación
 * abierta al lado, y dos pestañas con el mismo menú lateral son dos pestañas
 * que se confunden: cuál era cuál se decide mirando, y mirar cuesta cada vez.
 * Aquí el lateral es el índice de lo que hay escrito, y arriba hay una sola
 * puerta de vuelta.
 */

const { t, locale } = useI18n()
const route = useRoute()

// El índice lateral es el MISMO índice, y por tanto se filtra igual: lo que se
// enseña es lo que esta instancia monta, no lo que traía la imagen. Sin esto, el
// filtro del contenido no serviría de nada — el lateral seguiría nombrando los
// módulos que no están, y encima con enlace.
const { installed, asking } = useMountedModules()

const groups = computed(() =>
  filterGroups(asking.value ? [] : helpGroups(t, installed.value, locale.value), docsTerm.value, t))

/**
 * El tema anterior y el siguiente, en el orden del índice.
 *
 * El orden es el del índice de la izquierda y no otro: quien lee «siguiente»
 * espera lo que tiene debajo en el menú. Se calcula sobre los temas SIN filtrar
 * por el buscador — el buscador es una lupa, no un recorte del documento, y
 * escribir tres letras no puede cambiar a dónde lleva «siguiente».
 */
const around = computed(() => {
  const todos = (asking.value ? [] : helpGroups(t, installed.value, locale.value)).flatMap(group => group.topics)
  const at = todos.findIndex(topic => topic.id === route.params.topic)
  if (at < 0) return { prev: null, next: null }
  return { prev: todos[at - 1] ?? null, next: todos[at + 1] ?? null }
})

// ── El índice de esta página ─────────────────────────────────────────────────
//
// Se saca de los encabezados ya dibujados, no del Markdown. Es lo que permite
// que un tema escrito en `.md` y otro que es un componente Vue —el parte por
// plantilla lo es— tengan índice sin que ninguno declare nada, y lo que hace que
// el índice no pueda contradecir a lo que se ve: es literalmente lo que se ve.

const main = ref(null)
const outline = ref([])
const here = ref('')

function readOutline() {
  const root = main.value
  if (!root) { outline.value = []; return }

  // `h1` fuera: es el título de la página, y un índice cuya primera entrada es
  // el título de la página no ayuda a nadie.
  const found = [...root.querySelectorAll('h2, h3, h4, h5')]
  const items = outlineOf(found.map(el => ({ level: Number(el.tagName[1]), text: el.textContent.trim() })))

  // El identificador se pone aquí porque el renderizador de Markdown no lo pone
  // —dibuja un subconjunto a propósito— y sin `id` no hay a dónde saltar.
  found.forEach((el, index) => { el.id = items[index].id })

  outline.value = items
  spy()
}

/**
 * Cuál de los apartados se está leyendo, para marcarlo en el índice.
 *
 * El que haya pasado por arriba del todo más recientemente, y no «el que se ve»:
 * un apartado corto entre dos largos se vería a la vez que el siguiente, y la
 * marca iría saltando entre los dos cada vez que la rueda se mueve un pelo.
 */
/**
 * Lo que se ha pedido con un clic manda hasta que la persona vuelve a moverse.
 *
 * Los últimos apartados de un documento NO se pueden subir hasta arriba: la
 * página se acaba antes. Sin esto, pinchar «Quién puede hacer qué» desplazaba
 * bien y acto seguido marcaba el apartado de encima —que es el que de verdad
 * había pasado por el límite—, y el índice parecía contestar otra cosa a lo que
 * se le acababa de pedir.
 *
 * Se suelta con la rueda, con una tecla o con un dedo, y NO por tiempo: por
 * tiempo volvía a equivocarse solo, con la persona mirando y sin haber tocado
 * nada. El desplazamiento que provoca el propio clic no dispara ninguno de esos
 * tres, que es justo lo que hace que esto funcione.
 */
let pedido = null

function spy() {
  const root = main.value
  if (!root) return
  if (pedido) { here.value = pedido; return }

  // Por debajo de la barra de arriba, que es sticky y tapa los primeros píxeles.
  const límite = 96
  let actual = outline.value[0]?.id ?? ''
  for (const item of outline.value) {
    const el = root.querySelector(`#${CSS.escape(item.id)}`)
    if (el && el.getBoundingClientRect().top <= límite) actual = item.id
  }
  here.value = actual
}

function jumpTo(id) {
  const el = main.value?.querySelector(`#${CSS.escape(id)}`)
  if (!el) return

  pedido = id
  here.value = id

  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  // La dirección cambia sin navegar: es la que alguien copia para mandar este
  // apartado concreto, y hacerlo con el enrutador remontaría la página.
  history.replaceState(null, '', `#${id}`)
}

/**
 * El contenido llega tarde y en dos tiempos: el `.md` de un módulo se pide al
 * abrirlo y un tema de componente se resuelve aparte. Un `nextTick` sólo vería
 * el hueco, así que se vigila el sitio donde aparece.
 */
let observer = null

const suelta = () => { pedido = null }
const MOVIMIENTOS = ['wheel', 'touchstart', 'keydown']

onMounted(() => {
  observer = new MutationObserver(() => readOutline())
  if (main.value) observer.observe(main.value, { childList: true, subtree: true })
  readOutline()
  window.addEventListener('scroll', spy, { passive: true })
  for (const evento of MOVIMIENTOS) window.addEventListener(evento, suelta, { passive: true })
})

onBeforeUnmount(() => {
  observer?.disconnect()
  window.removeEventListener('scroll', spy)
  for (const evento of MOVIMIENTOS) window.removeEventListener(evento, suelta)
  // Lo buscado se queda en la documentación. Volver al ERP y entrar otra vez con
  // un filtro puesto de la visita anterior es la manera de creer que falta media
  // documentación.
  docsQuery.value = ''
})

// Cambiar de tema es cambiar de página: el índice de la anterior no vale, y la
// marca de «por aquí voy» tiene que volver arriba.
watch(() => route.params.topic, async () => {
  outline.value = []
  here.value = ''
  pedido = null
  await nextTick()
  readOutline()
})
</script>

<style scoped>
/* dvh y lo que se ve (ui-kit utils/viewport.js) en vez de vh: en un teléfono 100vh
   es la ventana sin la barra del navegador y, con el teclado fuera, la de detrás
   de las teclas. */
.docs { min-height: 100vh; min-height: 100dvh; background: var(--color-bg); display: flex; flex-direction: column; }

.docs__top {
  position: sticky; top: 0; z-index: 10;
  display: flex; align-items: center; gap: var(--space-5);
  padding: var(--space-3) var(--space-6);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface);
}
.docs__brand { display: inline-flex; align-items: center; gap: var(--space-2); font-weight: 600; flex: none; }
.docs__mark {
  display: grid; place-items: center; width: 1.5rem; height: 1.5rem;
  border-radius: 50%; background: var(--color-primary-soft); color: var(--color-primary);
  font-size: var(--text-sm);
}
.docs__back { color: var(--color-text-soft); font-size: var(--text-sm); flex: none; }

.docs__search {
  flex: 1; max-width: 26rem; margin: 0 auto;
  display: flex; align-items: center; gap: var(--space-2);
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--color-border); border-radius: var(--radius);
  background: var(--color-bg);
}
.docs__search:focus-within { border-color: var(--color-primary); }
.docs__searchIcon { color: var(--color-text-soft); font-size: var(--text-md); line-height: 1; }
.docs__searchInput {
  flex: 1; min-width: 0; border: 0; background: none; outline: none;
  color: var(--color-text); font: inherit; font-size: var(--text-sm);
  padding: var(--space-1) 0;
}
.docs__searchInput::-webkit-search-cancel-button { filter: grayscale(1) opacity(0.6); }

.docs__body { flex: 1; display: grid; grid-template-columns: 17rem minmax(0, 1fr); align-items: start; }

.docs__side {
  position: sticky; top: 3.5rem;
  max-height: calc(var(--app-height, 100dvh) - 3.5rem); overflow-y: auto;
  display: flex; flex-direction: column; gap: 2px;
  padding: var(--space-5) var(--space-4);
  border-right: 1px solid var(--color-border);
}
.docs__group {
  margin-top: var(--space-4); margin-bottom: var(--space-1);
  font-size: var(--text-xs); text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--color-text-soft); font-weight: 600;
}
.docs__link {
  padding: var(--space-2) var(--space-3); border-radius: var(--radius);
  color: var(--color-text); font-size: var(--text-sm); line-height: 1.35;
}
.docs__link:hover { background: var(--color-surface-2); }
.docs__link--on { background: var(--color-primary-soft); color: var(--color-primary); font-weight: 600; }
.docs__none { margin: var(--space-4) var(--space-3); color: var(--color-text-soft); font-size: var(--text-sm); }

/* El área de lectura: el texto con su medida, el índice de la página a la
   derecha, y el conjunto centrado en lo que haya de ancho. */
/* El texto arranca pegado al índice de la izquierda, no centrado en lo que
   sobra: centrarlo abría un pasillo vacío entre el árbol de navegación y el
   título que crecía con la pantalla, y lo que se leía entonces era una columna
   huérfana en mitad de la nada. Así el título empieza donde acaba el árbol, y
   el ancho que eso libera se lo queda el texto.

   Lo que el texto NO hace es crecer sin fin: pasados unos noventa caracteres por
   línea el ojo pierde el renglón al bajar, y esto está escrito para leerse
   entero. El resto de la pantalla grande lo aprovecha la tercera columna. */
.docs__reading {
  display: grid;
  grid-template-columns: minmax(0, 56rem) 17rem;
  justify-content: start;
  gap: var(--space-8);
  /* La escala de espacios NO tiene 7 ni 9 —salta de 6 a 8 y de 8 a 10— y una
     variable que no existe invalida la declaracion ENTERA: esto era
     `padding: 0` y el titulo arrancaba pegado al borde del menu. No falla, no
     avisa, y solo se ve midiendo. */
  padding: var(--space-8) var(--space-8) var(--space-10);
}

/* Ancho de lectura. Un párrafo de 1.400 píxeles de largo no lo lee nadie, y
   esto existe para leerse entero. */
.docs__main { min-width: 0; }

/* La barra de arriba es fija: sin esto, saltar a un apartado lo deja justo
   DEBAJO de ella y lo primero que se lee del apartado es su segunda línea. */
.docs__main :deep(h2),
.docs__main :deep(h3),
.docs__main :deep(h4),
.docs__main :deep(h5) { scroll-margin-top: 5rem; }

.docs__reading--wide { grid-template-columns: minmax(0, 88rem); }

.docs__around {
  display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-4);
  margin-top: var(--space-10); padding-top: var(--space-6);
  border-top: 1px solid var(--color-border);
}
.docs__step {
  display: flex; flex-direction: column; gap: var(--space-1);
  padding: var(--space-4);
  border: 1px solid var(--color-border); border-radius: var(--radius-lg);
  color: var(--color-text);
}
.docs__step:hover { border-color: var(--color-primary); }
.docs__step--next { text-align: right; }
.docs__stepWay { color: var(--color-text-soft); font-size: var(--text-xs); }
.docs__stepName { font-weight: 600; line-height: 1.3; }

.docs__outline {
  position: sticky; top: 5rem;
  max-height: calc(var(--app-height, 100dvh) - 7rem); overflow-y: auto;
  display: flex; flex-direction: column; gap: 1px;
  padding-left: var(--space-4);
  border-left: 1px solid var(--color-border);
}
.docs__outlineTitle {
  margin-bottom: var(--space-2);
  font-size: var(--text-xs); text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--color-text-soft); font-weight: 600;
}
.docs__anchor {
  padding: var(--space-1) 0;
  color: var(--color-text-soft); font-size: var(--text-sm); line-height: 1.4;
  border-left: 2px solid transparent; margin-left: calc(var(--space-4) * -1 - 1px);
  padding-left: var(--space-4);
}
.docs__anchor--2 { padding-left: var(--space-6); font-size: var(--text-xs); }
.docs__anchor:hover { color: var(--color-text); }
.docs__anchor--on { color: var(--color-primary); border-left-color: var(--color-primary); }

/* Sin sitio para la tercera columna, se va la de la derecha: el texto es lo que
   no puede faltar. */
/* Con el dedo, el buscador a 16px: más pequeño, iOS amplía la página al
   enfocarlo y no la devuelve (--text-field de ui-kit tokens.css). */
@media (pointer: coarse) {
  .docs__searchInput { font-size: var(--text-field); }
}

@media (max-width: 86rem) {
  .docs__reading { grid-template-columns: minmax(0, 56rem); }
  .docs__outline { display: none; }
}

@media (max-width: 60rem) {
  .docs__top { flex-wrap: wrap; gap: var(--space-3); }
  .docs__search { order: 3; flex-basis: 100%; max-width: none; margin: 0; }
  .docs__body { grid-template-columns: 1fr; }
  .docs__side {
    position: static;
    /* Con veinticinco módulos escritos, un índice suelto arriba son veinticinco
       entradas por delante del texto: quien abre un enlace en el móvil llega a
       una lista, no a lo que venía a leer. Se queda en un tercio de pantalla y
       se desplaza dentro. */
    max-height: 33vh; overflow-y: auto;
    border-right: 0; border-bottom: 1px solid var(--color-border);
  }
  .docs__reading { padding: var(--space-5) var(--space-4) var(--space-8); }
}
</style>
